import React from "react";
import { Button, InformationBox } from "kubra-ux-forge";
import { useDispatch, useSelector } from "react-redux";
import { PageLoader } from "components/ezpay/common/page-loader";
import { getBiller, getLabelsWithKrn } from "selectors";
import { ContactAndTocFooter } from "components/ezpay/common/contact-and-toc-footer";
import { useTranslation } from "react-i18next";
import { PAYABLE_REVIEW_NAMESPACE } from "constants/i18n-namespaces";
import { useNavigate } from "react-router";
import { getPaymentIntent } from "selectors/paymentIntent";
import routing from "routing";
import IamApi from "api/IamApi";
import { USER_LOGOUT } from "actions/userActions";
import { getPayableSession } from "selectors/payableSession";
import { getLabelWithKrn } from "utilities/helper";
import { PayableCardHeader } from "components/ezpay/payables/payable-card-header";
import { PaymentSuccessSummary } from "../payment-success-summary";
import { ExtensionFieldValue } from "types/payable";

function getExtensionFieldValue(field: ExtensionFieldValue) {
  if (field.type === "PhoneNumber") {
    const phonePrefix = "tel:";
    return field.value.startsWith(phonePrefix)
      ? field.value.slice(phonePrefix.length)
      : field.value;
  } else if (field.type === "Email") {
    const emailPrefix = "mailto:";
    return field.value.startsWith(emailPrefix)
      ? field.value.slice(emailPrefix.length)
      : field.value;
  } else if (field.type === "Address" && typeof field.value === "object" && field.value !== null) {
    const { streetAddress, locality, region, postalCode } = field.value;
    return [streetAddress, locality, region, postalCode]
      .filter(Boolean)
      .join(", ");
  } else {
    return field.value;
  }
}

export const PaymentSuccess = () => {
  const { t } = useTranslation(PAYABLE_REVIEW_NAMESPACE);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const payableSession = useSelector(getPayableSession);
  const paymentIntentState = useSelector(getPaymentIntent);

  const biller = useSelector(getBiller);
  const labelSourceWithKrn = useSelector(getLabelsWithKrn);
  const isCallCenterChannel = payableSession?.channel === "CallCenter";

  const handleMakeAnotherPayment = async () => {
    await IamApi.ezpay.logout();
    dispatch({ type: USER_LOGOUT });
    navigate(routing.index());
  };

  const email = paymentIntentState?.receiptInfo?.email?.trim() || "";
  const mobile = paymentIntentState?.receiptInfo?.mobileNumber?.trim() || "";

  return (
    <div className="payable-ez-pay-balance-frame" data-cy="ez-pay-review-frame">
      <PageLoader />
      <div className="ez-pay-review-container" data-cy="ez-pay-review-top">
        <PayableCardHeader showBreadcrumb={false} />
        <div className="ez-pay-payment-success-top-row">
          <h2 data-cy="payment-completed-successfully-heading">{t("payment-completed")}</h2>
          {(email || mobile) && (
            <p>
              {t("receipt-info")}{" "}
              {email && <b>{email}</b>}
              {email && mobile && <> {t("and")} </>}
              {mobile && <b>{mobile}</b>}
            </p>
          )}
        </div>
        {payableSession?.payable?.extensionFields &&
          Object.keys(payableSession.payable.extensionFields).length > 0 ? (
          <div
            className="ez-pay-payment-top-row"
            data-cy="ez-pay-review-top-row"
          >
            {Object.entries(payableSession.payable.extensionFields || {}).map(
              ([fieldKey, field]) => (
                <div
                  key={fieldKey}
                  className="ez-pay-extension-field"
                  data-cy={`ez-pay-review-extension-field-${fieldKey}`}
                >
                  <div
                    className="ez-pay-extension-field-label"
                    data-cy={`ez-pay-review-extension-label-${fieldKey}`}
                  >
                    {getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, payableSession?.payable?.typeKrn ?? null, fieldKey)}:
                  </div>{" "}
                  {getExtensionFieldValue(field)}
                </div>
              )
            )}
          </div>
        ) : (
          <hr className="ez-pay-review-separator" />
        )}
        <div className="ez-pay-review-content" data-cy="ez-pay-review-content">
          <PaymentSuccessSummary />

          {!isCallCenterChannel && (
            <div className="ez-pay-review-buttons-container">
            <Button
              type="button"
              onClick={handleMakeAnotherPayment}
              dataCy="button-make-another-payment"
            >
              {t("make-another-payment")}
            </Button>
            </div>
          )}
          {isCallCenterChannel && (
            <div
              className="ez-pay-payable-row"
              style={{ marginLeft: 0, marginRight: 0 }}
              data-cy="call-center-restriction-tooltip"
            >
              <InformationBox 
                messageBody={t("call-center-session-instructions")}
                variant="info"
              />
            </div>
          )}
          <div className="ez-pay-payable-row">
            <ContactAndTocFooter biller={biller} />
          </div>
        </div>
      </div>
    </div>
  );
};
